/** ---------------------------------------------------------------------------------------
 * --  LICENSE AGREEMENT -- *
 This software is licensed under the following terms:

 * -- GRANT OF LICENSE | RESTRICTIONS -- *
 Subject to the terms and conditions of this license, the copyright holders
 hereby grant you a non-exclusive, non-transferable, revocable license to use,
 copy, modify, and distribute the software.
 You are expressly prohibited from selling or commercially exploiting the software.
 Unauthorized use, reproduction, or distribution of the software, or any part
 thereof, is strictly prohibited.

 * -- AUTHORIZED USERS | INTELLECTUAL PROPERTY -- *
 This license is granted only to the individual or entity to whom the software is
 directly distributed by the copyright holders. Any use by other individuals or
 entities is unauthorized and in violation of this license. The software is
 protected by copyright and other intellectual property laws.
 The copyright holders retain all rights, title, and interest in the software.

 * -- GOVERNING LAW -- *
 This license is governed by and construed in accordance with the laws of Romania.
 Any legal action or proceeding related to this license shall be brought exclusively
 in the courts of Romania.

 [Weddify x Diaphan Stationery] © [2024] All rights reserved.
 -------------------------------------------------------------------------------------- **/
'use client'

import React from 'react';
import AppDropdown from "@/components/header/AppDropdown";
import {FaAt, FaBagShopping, FaBook, FaQuestion, FaRegCircleUser} from "react-icons/fa6";
import {FiAlignJustify} from "react-icons/fi";
import {FaCoins} from "react-icons/fa";
import {appContactPageURL, dashURL} from '@/utils/AppConfig';
import Link from 'next/link';
import AppStatic from "@/utils/AppStatic";
import Image from "next/image";
import weddifyLogo from "@/assets/images/weddify-logo.webp";
import weddifyLogin from "@/assets/images/weddify-login.webp";

const AppHeader: React.FC = () => {

    return (
        <header>
            <nav
                className="w-full fixed top-0 z-20 flex flex-row gap-4 items-center justify-between bg-white/10 px-2 backdrop-blur-xl">
                <Link href={'/'}>
                    <Image src={weddifyLogo}
                           className="w-40 ml-1 md:ml-4 hover:scale-105 transition-transform ease-in-out duration-300"
                           alt="Weddify by Diaphan" priority={true}/>
                </Link>
                <div
                    className="mr-1 md:mr-4 relative flex h-[61px] w-[180px] grow items-center lg:justify-around justify-center gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 md:w-[180px] sm:grow-0 xl:w-[180px] border-[0.5px] border-[#DAD8D8]">
                    <div className="flex gap-4 cursor-pointer items-center">
                        <span
                            className="text-mono-1000 hover:scale-105 transition-transform ease-in-out duration-300">
                            <Link href={appContactPageURL}>
                                <FaAt className={"h-6 w-6"}/>
                            </Link>
                        </span>
                        <AppDropdown
                            button={
                                <span
                                    className="text-xl text-mono-1000 hover:scale-105 transition-transform ease-in-out duration-300">
                                    <FiAlignJustify className="h-7 w-7"/>
                                </span>
                            }
                            children={
                                <div
                                    className="flex h-auto w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500">
                                    <div className="mt-3 ml-4 flex flex-col">
                                        <Link
                                            className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300"
                                            href="/#app-plans">
                                            <FaBagShopping/> {AppStatic.pricesHeader.ro.translation}
                                        </Link>
                                        <Link href="/contact" onClick={() => window.scrollTo(0, 0)}>
                                            <div
                                                className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300">
                                                <FaAt/> {AppStatic.contactHeader.ro.translation}
                                            </div>
                                        </Link>
                                        <a href={dashURL} target="_blank"
                                           rel="noreferrer"
                                           className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300">
                                            <FaRegCircleUser/> {AppStatic.appLoginTitle.ro.translation}
                                        </a>
                                        <a href="https://diaphan.ro/despre-noi/" target="_blank"
                                           rel="noreferrer"
                                           className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300">
                                            <FaBook/> {AppStatic.appStory.ro.translation}
                                        </a>
                                        <Link
                                            className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300"
                                            href="/#app-faq">
                                            <FaQuestion/> {AppStatic.appFaqTitle.ro.translation}
                                        </Link>
                                        <Link href="/ambassador" onClick={() => window.scrollTo(0, 0)}>
                                            <div
                                                className="mb-4 text-sm uppercase flex gap-1 items-center hover:scale-105 transition-transform ease-in-out duration-300">
                                                <FaCoins/> {AppStatic.ambassadorHeader.ro.translation}
                                            </div>
                                        </Link>
                                    </div>
                                </div>
                            }
                            classNames={"py-2 top-8 -left-[180px] w-max"}
                        />
                        <a href={dashURL} target="_blank" rel="noreferrer">
                            <Image
                                className="h-10 w-10 rounded-full cursor-pointer ml-2 hover:scale-105 transition-transform ease-in-out duration-300"
                                src={weddifyLogin}
                                alt="Weddify By Diaphan Login"
                                priority={true}
                            />
                        </a>
                    </div>
                </div>
            </nav>
        </header>
    );
}

export default AppHeader;
